import React, { useState, useEffect, useRef } from "react";
import { showToast, ChunkErrorBoundary, useConfirm } from "../components/errors";
import {
  useLocation as useRouterLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { useLocation as useLocationContext } from "../context/LocationContext";
import usePoll from "../hooks/usePoll";
import useGlobalSearch, { PAGE_CATALOG } from "../hooks/useGlobalSearch";
import { recordVisit } from "../utils/searchHistory";
import { useSocketEvent } from "../hooks/useSocketEvent";
import { useAuth } from "../context/AuthContext";
import { API_URL, getAuthHeaders, fetchWithAuth, setAccessToken, clearAccessToken } from "../utils/api";
import SearchResults from "../components/SearchResults";
import HelpMenu from "../components/HelpMenu";
import InboxMenu from "../components/InboxMenu";
import PagePermissionsModal from "../components/PagePermissionsModal";
import MessagesMenu from "../components/messaging/MessagesMenu";
import NewOrderHost, { openNewOrder } from "../components/NewOrder/NewOrderHost";
import { useMobileNav, MobileNavButton, MobileNavBackdrop, PHONE_QUERY } from "../components/MobileNav";
import useSidebarPrefs, { SIDEBAR_WIDTH_DEFAULT } from "../hooks/useSidebarPrefs";
import { readPref, writePref } from "../utils/safeStorage";
import "../components/NewOrder/AddOrderMenu.css";
import "../components/shopmonkey/ScopeToggle.css";
import DailyMessage from "../components/DailyMessage";
import SidebarSection from "../components/SidebarSection";
import { SocketProvider } from "../context/SocketContext";
import { TimerProvider } from "../context/TimerContext";
import { CustomerPreviewProvider } from "../context/CustomerPreviewContext";
import { SiteAlertsProvider } from "../components/alerts/SiteAlertsContext";
import SiteAlertBanners from "../components/alerts/SiteAlertBanners";
import SiteAlertCards from "../components/alerts/SiteAlertCards";
import OsrAlerts from "../components/osr/OsrAlerts";
import BookingAlerts from "../components/calls/BookingAlerts";
import LiveChatAlerts from "../components/webChat/LiveChatAlerts";
import { useNewLeadCount } from "../components/osr/osrAlertStore";
import TimerBar from "../components/TimerBar";
import { startUploadQueue, setUploadQueueUser } from "../utils/uploadQueue";
import { startOutbox, setOutboxOwner, outboxCount, flushOutbox } from "../utils/offlineOutbox";
import { setOfflineOrdersOwner } from "../utils/offlineOrders";
import OutboxStatus from "../components/offline/OutboxStatus";
// import CallNotificationToast from "../components/calls/CallNotificationToast"; // disabled — will revisit
import SaveIndicator from "../components/OrderDetail/shared/SaveIndicator";
import OrderPageSwitch from "../components/OrderPage/OrderPageSwitch";
import CustomerPreviewDrawer from "../components/CustomerPreviewDrawer";

// Admin pages — lazy-loaded, so the main bundle carries the shell and not
// every page. Sep 2026: main.js was 3.5 MB (929 kB gzip) with only three lazy
// routes, and every hard refresh re-downloaded all of it. An earlier attempt
// (89b78ee) was reverted for ChunkLoadErrors after deploys — a tab open
// across a deploy asks for chunk hashes the new build no longer serves.
// utils/lazyWithRetry.js is the retry boundary that revert asked for: one
// guarded hard reload fetches the fresh index.html. Dashboard stays static —
// it's the default landing page, and one round trip fewer on first paint
// matters more than its weight.
import DashboardPage from "../pages/admin/Dashboard";
import LocationDropdownPanel from "../components/LocationDropdownPanel";
import lazyWithRetry from "../utils/lazyWithRetry";
const EditProfileModal = lazyWithRetry(() => import("../components/settings/Users/EditProfileModal"));
const ShopmonkeyLive = lazyWithRetry(() => import("../pages/admin/ShopmonkeyLive"));
const SalesIntakeOsr = lazyWithRetry(() => import("../pages/admin/SalesIntakeOsr"));
const SalesIntakeEstimates = lazyWithRetry(() => import("../pages/admin/SalesIntakeEstimates"));
const HelpSubmissionsPage = lazyWithRetry(() => import("../pages/admin/HelpSubmissions"));
const CustomersPage = lazyWithRetry(() => import("../pages/admin/Customers"));
const CustomerDetail = lazyWithRetry(() => import("../pages/admin/CustomerDetail"));
const OrderDetail = lazyWithRetry(() => import("../OrderDetail"));
const AppointmentsPage = lazyWithRetry(() => import("../pages/admin/Appointments"));
const TimeClocksPage = lazyWithRetry(() => import("../pages/admin/TimeClocksPage"));
const OrderWorkflowPage = lazyWithRetry(() => import("../pages/admin/OrderWorkflow"));
const PaymentTerminalPage = lazyWithRetry(() => import("../pages/admin/PaymentTerminal"));
const PartsInventoryPage = lazyWithRetry(() => import("../pages/admin/PartsInventory"));
const SettingsPage = lazyWithRetry(() => import("../pages/admin/Settings"));
const PoseidonPlusPage = lazyWithRetry(() => import("../pages/admin/PoseidonPlus"));
const SalesSubmissionsPage = lazyWithRetry(() => import("../pages/admin/SalesSubmissions"));
const PaymentEventLogPage = lazyWithRetry(() => import("../pages/admin/PaymentEventLog"));
const PaymentVaultTestPage = lazyWithRetry(() => import("../pages/admin/PaymentVaultTest"));
const WorkHeatMapPage = lazyWithRetry(() => import("../pages/admin/WorkHeatMap"));
const DispatchPage = lazyWithRetry(() => import("../pages/admin/Dispatch"));
const GeozonesPage = lazyWithRetry(() => import("../pages/admin/Geozones"));
const TeamMessagingPage = lazyWithRetry(() => import("../pages/admin/TeamMessaging"));
const LiveCallsPage = lazyWithRetry(() => import("../pages/admin/LiveCalls"));
const CustomerServicePage = lazyWithRetry(() => import("../pages/admin/CustomerService"));
const DigPage = lazyWithRetry(() => import("../pages/admin/Dig"));
const VesselsPage = lazyWithRetry(() => import("../pages/admin/Vessels"));
const VesselDetail = lazyWithRetry(() => import("../pages/admin/VesselDetail"));
const EngineDetail = lazyWithRetry(() => import("../pages/admin/EngineDetail"));
// Reports and Marketing Hub pull in recharts (~100 kB gzip) — same treatment.
const ReportsPage = lazyWithRetry(() => import("../pages/admin/Reports"));
const MarketingHubPage = lazyWithRetry(() => import("../pages/admin/MarketingHub"));

const ROLE_LABELS = {
  technician_manager: "Technician Manager",
  technician: "Technician",
  sales_manager: "Sales Manager",
  service_writer: "Service Writer",
  sales_support: "Sales Support",
};

const PREVIEWABLE_ROLES = [
  { value: "technician_manager", label: "Technician Manager" },
  { value: "technician", label: "Technician" },
  { value: "sales_manager", label: "Sales Manager" },
  { value: "service_writer", label: "Service Writer" },
  { value: "sales_support", label: "Sales Support" },
];

// Rendered inside <SocketProvider> (AdminLayout's own hooks sit above it):
// nudges the sidebar badges the moment a relevant socket event lands, which
// is what lets their polls drop to slow reconciles. A missing handler means
// the user can't see that badge (useSocketEvent skips it).
function BadgeSocketRefresh({ onCallEvent, onSalesEvent }) {
  useSocketEvent("call:incoming", onCallEvent);
  useSocketEvent("call:answered", onCallEvent);
  useSocketEvent("call:ended", onCallEvent);
  useSocketEvent("call:enriched", onCallEvent);
  useSocketEvent("call:missed", onCallEvent);
  useSocketEvent("call:removed", onCallEvent);
  useSocketEvent("sales_submission:changed", onSalesEvent);
  return null;
}

function AdminLayout({ user, onLogout }) {
  const routerLocation = useRouterLocation();
  const navigate = useNavigate();
  const {
    locations: accessibleLocations,
    selectedLocationId,
    selectedLocation: currentLocation,
    selectLocation,
    getLocationHeaders,
    loading: locationLoading,
    company,
    isBridgeMode,
    hasBridgeAccess,
  } = useLocationContext();

  // Get auth context: permissions + legacy role flags (role flags still exposed
  // for UI labeling like "You are signed in as Admin" but gating uses can()).
  const {
    isAdmin, isSalesManager, isServiceWriter, isSalesSupport, isTechnicianManager,
    viewAsRole, setViewAsRole, realIsAdmin,
    viewAsUserId, viewAsUserData, previewAsUser,
    can, offlineSession,
  } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirm();

  // User preview search state (only used by the dropdown)
  const [userPreviewSearch, setUserPreviewSearch] = useState("");
  const [previewUsers, setPreviewUsers] = useState([]);

  // Access gates — now driven by permissions catalog instead of role checks.
  // Each gate maps to a top-level "access" permission; individual buttons/tabs
  // within a page use more granular permissions inside their own components.
  const canAccessAdminPages = can('dashboard.access'); // Dashboard + admin cluster
  const canAccessCustomers = can('customers.access');
  const canAccessPoseidonPlus = can('plus.access');
  const canAccessShopmonkey = can('shopmonkey.access');
  // Customer Service area: reachable with the area permission OR any of its
  // tab permissions, so a user granted a single tab (e.g. Disputes only)
  // isn't locked out of the page shell.
  // Just the area key: every tab permission now hangs off it
  // (permissions-catalog.js PERMISSION_DEPENDENCIES), so a tab can't be true
  // while the area is false and the old OR-chain could never differ.
  const canAccessCustomerService = can('customer_service.access');
  const canAccessDig = can('dig.access');
  // Marketing Hub: ONE umbrella permission opens the hub; the area keys
  // (ads/projects/events/inventory/field/leads) pick the tabs inside.
  // Role defaults grant it wherever an area default exists, and migration
  // 130 backfilled it for per-user area grants.
  const canAccessMarketingHub = can('marketing.hub.access');
  // Parts & Inventory: two areas share the page, each with its own gate.
  // Parts Orders (parts_orders.access) is live; the Inventory / Purchase
  // Orders / Returns tabs (parts_inventory.access) are still soft-launched.
  // Either one opens the page; pages/admin/PartsInventory.js picks the tabs.
  const canAccessPartsInventory = can('parts_inventory.access') || can('parts_orders.access');

  // Poseidon Live writer scope ("All writers" vs "Mine") — persisted per user,
  // toggled from the profile dropdown, consumed by <ShopmonkeyLive/>.
  const canSeeAllWriters = can('shopmonkey.view_all_writers');
  const [pagePermsOpen, setPagePermsOpen] = useState(false);
  // Open critical System Health incidents → a count on the Settings entry.
  // Polls once a minute (same cadence as the Alert Center inbox), only for
  // people who can open the page.
  const canSeeHealth = can("system_health.access");
  const [openCritical, setOpenCritical] = useState(0);
  useEffect(() => { if (!canSeeHealth) setOpenCritical(0); }, [canSeeHealth]);
  usePoll(async () => {
    const res = await fetchWithAuth("/admin/system-health/badge");
    if (!res.ok) return false; // the page shows the real state; a missed poll is fine
    const b = await res.json();
    setOpenCritical(Number(b.openCritical) || 0);
  }, 60_000, { enabled: canSeeHealth });
  // Inspection photos still on their way (utils/uploadQueue.js) resume as
  // soon as the app is open — after a reload, without visiting the order.
  // They're the signed-in person's: on a shared iPad, nobody else's go out
  // under this sign-in.
  useEffect(() => {
    setUploadQueueUser(user?.id);
    startUploadQueue();
    return () => setUploadQueueUser(null); // signed out: nobody's go out until the next person is set
  }, [user?.id]);
  // Saves made with no signal (utils/offlineOutbox.js) go out the same way,
  // and someone in the work view keeps the orders they open for no signal
  // (utils/offlineOrders.js) — the technician's offline mode.
  const keepsOrders = can("orders.work") && !can("orders.edit");
  useEffect(() => {
    setOutboxOwner(user?.id);
    setOfflineOrdersOwner(user?.id, keepsOrders);
    startOutbox();
    return () => { setOutboxOwner(null); setOfflineOrdersOwner(null, false); };
  }, [user?.id, keepsOrders]);
  // Signing out sends what waits first; what can't go stays on this device
  // for their next sign-in here — said before they go.
  const signOut = async () => {
    setProfileDropdownOpen(false);
    if (outboxCount() > 0) {
      showToast.info("Sending what's waiting before you sign out…");
      const left = await flushOutbox(8000);
      if (left > 0) {
        const ok = await confirm({
          title: "Sign out with saves waiting?",
          message: `${left === 1 ? "1 save hasn't" : `${left} saves haven't`} reached Chariot yet. ${left === 1 ? "It stays" : "They stay"} on this device and ${left === 1 ? "goes" : "go"} the next time you sign in here.`,
          confirmText: "Sign out",
          cancelText: "Stay signed in",
        });
        if (!ok) return;
      }
    }
    onLogout();
  };
  const [smWriterScope, setSmWriterScope] = useState('all');
  useEffect(() => {
    if (!canSeeAllWriters) return;
    fetchWithAuth('/admin/shopmonkey/prefs')
      .then((r) => r.json())
      .then((j) => {
        if (j.success && j.writer_scope) setSmWriterScope(j.writer_scope);
      })
      .catch(() => {});
  }, [canSeeAllWriters]);
  const changeSmWriterScope = (s) => {
    if (s === smWriterScope) return;
    setSmWriterScope(s);
    fetchWithAuth('/admin/shopmonkey/prefs', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ writer_scope: s }),
    }).catch(() => {});
  };
  const canAccessSalesSubmissions = can('sales_submissions.access');
  // Sales Intake opens for the call tabs (calls.access), the OSR tab
  // (osr.access) OR the Estimates tab (sales_estimates.access): someone who
  // works leads or estimates without call access still gets in and sees the
  // tabs they hold.
  const canAccessLiveCalls = can('calls.access') || can('osr.access') || can('sales_estimates.access');
  const newLeadCount = useNewLeadCount(); // published by <OsrAlerts/>

  // Redirect non-admin users from admin-only pages to their default accessible page
  // Map URL page -> specific permission key that gates it
  const pagePermissionMap = {
    dashboard: 'dashboard.access',
    'order-workflow': 'workflow.access',
    'payment-terminal': 'terminal.access',
    orders: 'orders.access',
    appointments: 'appointments.access',
    dispatch: 'dispatch.access',
    reports: 'reports.access',
    // 'marketing' is gated inline via canAccessMarketingHub
    // (marketing.hub.access — the umbrella; area keys gate tabs inside).
    // 'parts-inventory' is gated inline via canAccessPartsInventory (either
    // of two permissions opens it).
    settings: 'settings.access',
    timeclocks: 'timeclocks.access',
    customers: 'customers.access',
    vessels: 'vessels.access',
    'poseidon-plus': 'plus.access',
    shopmonkey: 'shopmonkey.access',
    // 'sales-intake' is gated inline via canAccessLiveCalls (calls.access,
    // osr.access or sales_estimates.access — any one opens the page).
    'sales-submissions': 'sales_submissions.access',
    'payment-events': 'payments.events.view',
    'payment-vault-test': 'payments.processor.switch',
    'work-heat-map': 'adminTools.workHeatMap.access',
    geozones: 'adminTools.geozones.access',
    // 'customer-service' is gated inline via canAccessCustomerService (OR of
    // area access + relocated sub-page permissions).
    'team-messaging': 'messaging.access',
  };

  useEffect(() => {
    // Strip the /app prefix ANCHORED-only (here and in the URL helpers below).
    // The old unanchored .replace("/app", "") also ate the "/app" inside nested
    // segments — /app/shopmonkey/APPointments became "shopmonkeyointments",
    // matching no page and rendering a blank screen.
    const path = routerLocation.pathname.replace(/^\/app\/?/, "");
    const page = path.split("/")[0] || "dashboard";
    // Legacy URL: the page was renamed Live Calls → Sales Intake. Keep old
    // bookmarks/links working, sub-path and query included.
    if (page === "live-calls") {
      navigate(routerLocation.pathname.replace(/^\/app\/live-calls/, "/app/sales-intake") + routerLocation.search, { replace: true });
      return;
    }
    // Legacy URL: the OSR tab moved from Poseidon Live to Sales Intake
    // (Sep 30 2026). Alert links and bookmarks still work.
    if (page === "shopmonkey" && path.split("/")[1] === "osr") {
      navigate("/app/sales-intake/osr" + routerLocation.search, { replace: true });
      return;
    }
    // Legacy URL: Settings › Poseidon Plus, the old plan editor, was removed
    // (Oct 2026). Plans and members are on the Poseidon Plus page, which also
    // opens a ?member= link.
    if (page === "settings" && path.split("/")[1] === "poseidon-plus") {
      navigate("/app/poseidon-plus" + routerLocation.search, { replace: true });
      return;
    }

    const requiredPerm = pagePermissionMap[page];
    // If the page is gated and the user doesn't have the permission, redirect them.
    if (requiredPerm && !can(requiredPerm)) {
      // Find a fallback page the user DOES have access to (prefer poseidon-plus then customers)
      if (can('plus.access')) {
        navigate("/app/poseidon-plus", { replace: true });
      } else if (can('customers.access')) {
        navigate("/app/customers", { replace: true });
      }
      // Otherwise leave them on the current URL — Access Denied UI handles it.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.pathname, navigate]);

  // Get current page from URL path
  const getCurrentPage = () => {
    const path = routerLocation.pathname.replace(/^\/app\/?/, "");
    // Handle nested routes (e.g., poseidon-plus/subscribers -> poseidon-plus)
    const basePage = path.split("/")[0];
    return basePage || "dashboard";
  };

  // Get customer ID from URL for customer detail page (e.g., /app/customers/123)
  const getCustomerId = () => {
    const path = routerLocation.pathname.replace(/^\/app\/?/, "");
    const parts = path.split("/");
    if (parts[0] === "customers" && parts[1]) {
      return parts[1];
    }
    return null;
  };

  // Get order ID from URL for order detail page (e.g., /app/orders/123)
  const getOrderId = () => {
    const path = routerLocation.pathname.replace(/^\/app\/?/, "");
    const parts = path.split("/");
    if (parts[0] === "orders" && parts[1] && parts[1] !== "new") {
      return parts[1];
    }
    return null;
  };

  // Get vehicle ID from URL for vessel detail page (e.g., /app/vessels/123)
  const getVehicleId = () => {
    const path = routerLocation.pathname.replace(/^\/app\/?/, "");
    const parts = path.split("/");
    if (parts[0] === "vessels" && parts[1] && parts[1] !== "engine") {
      return parts[1];
    }
    return null;
  };

  // Get engine ID from URL (e.g., /app/vessels/engine/123)
  const getEngineId = () => {
    const path = routerLocation.pathname.replace(/^\/app\/?/, "");
    const parts = path.split("/");
    if (parts[0] === "vessels" && parts[1] === "engine" && parts[2]) {
      return parts[2];
    }
    return null;
  };

  // Get report key from URL for a specific report (e.g., /app/reports/sales-summary)
  const getReportKey = () => {
    const path = routerLocation.pathname.replace(/^\/app\/?/, "");
    const parts = path.split("/");
    if (parts[0] === "reports" && parts[1]) {
      return parts[1];
    }
    return null;
  };

  const currentPage = getCurrentPage();
  // Sales Intake's tab (/app/sales-intake/<tab>): picks OSR or Estimates for
  // someone without the call tabs.
  const salesIntakeTab = currentPage === "sales-intake" ? routerLocation.pathname.replace(/^\/app\/?/, "").split("/")[1] || null : null;
  // Remember top-level page visits for the ⌘K box's "Recent" / "Most visited"
  // (record pages — customers, orders, vessels — remember themselves on load).
  useEffect(() => {
    const page = PAGE_CATALOG.find((p) => p.key === currentPage);
    if (page) recordVisit({ type: "page", id: page.key, title: page.label, url: `/app/${page.key}` });
  }, [currentPage]);
  const customerId = getCustomerId();
  const orderId = getOrderId();
  const vehicleId = getVehicleId();
  const engineId = getEngineId();
  const reportKey = getReportKey();

  const setCurrentPage = (page) => {
    navigate(`/app/${page}`);
  };

  // Global search — state, debounce, keyboard nav and recents live in the
  // hook; the dropdown is components/SearchResults.js.
  const search = useGlobalSearch({ can });
  const { query: searchQuery, setQuery: setSearchQuery, open: searchFocused, setOpen: setSearchFocused, close: closeSearch } = search;
  search.selectRef = useRef(null);
  const searchInputRef = useRef(null);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ssUncapturedCount, setSsUncapturedCount] = useState(0);
  const [activeCallCount, setActiveCallCount] = useState(0);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = readPref("chariot-dark-mode");
    return saved === "true";
  });

  // Sidebar collapsed/expanded + width, per user (hooks/useSidebarPrefs).
  const { sidebarOpen, setSidebarOpen, setSidebarWidth, sidebarResizing, handleSidebarResizeStart } = useSidebarPrefs(user?.email);
  // Phone width: the sidebar is a drawer behind the top bar's menu button,
  // shown expanded while open whatever the desktop preference.
  const [mobileNavOpen, setMobileNavOpen, isPhone] = useMobileNav();
  const sidebarExpanded = sidebarOpen || isPhone;
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [roleViewDropdownOpen, setRoleViewDropdownOpen] = useState(false);
  const roleViewDropdownRef = useRef(null);
  // Inbox dropdown — merged notifications + messages. UI shell only,
  // mock data lives next to the JSX. Not wired to a real feed yet.
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxTab, setInboxTab] = useState("all");
  const inboxRef = useRef(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);

  // Apply dark mode to document
  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      darkMode ? "dark" : "light",
    );
    writePref("chariot-dark-mode", darkMode);
  }, [darkMode]);

  // Poll uncaptured sales submissions count for sidebar badge
  // Count only, every 30 s — plus an instant refresh on every
  // sales_submission:changed socket event (BadgeSocketRefresh below).
  const refreshSalesBadge = usePoll(async () => {
    const res = await fetchWithAuth("/admin/sales-submissions/uncaptured?count_only=1");
    if (!res.ok) return false;
    const json = await res.json();
    if (json.success) setSsUncapturedCount(Number(json.count) || 0);
  }, 30000, { enabled: canAccessSalesSubmissions });

  // Poll active calls count for sidebar badge
  // Count only, every 30 s — plus an instant refresh on every call socket
  // event (CallsBadgeSocketRefresh below), so the badge is live without the
  // old 10 s full-payload poll from every admin tab.
  const refreshCallsBadge = usePoll(async () => {
    const res = await fetchWithAuth("/admin/calls/active?count_only=1");
    if (!res.ok) return false;
    const json = await res.json();
    if (json.success) setActiveCallCount(Number(json.count) || 0);
  }, 30000, { enabled: canAccessLiveCalls });

  // Update page title
  useEffect(() => {
    const pageNames = {
      dashboard: "Dashboard",
      customers: "Customers",
      orders: "Orders",
      appointments: "Appointments",
      reports: "Reports",
      marketing: "Marketing Hub",
      "parts-inventory": "Parts & Inventory",
      workflow: "Workflow",
      "poseidon-plus": "Poseidon+",
      settings: "Settings",
      services: "Services",
      shopmonkey: "Poseidon Live",
      "sales-intake": "Sales Intake",
      "sales-submissions": "Sales Submissions",
      "payment-events": "Payment Events",
      "payment-vault-test": "Payment Vault Test",
      "payment-terminal": "Payment Terminal",
      "work-heat-map": "Work Heat Map",
      dispatch: "Live Dispatch",
      geozones: "Service Geozones",
      "customer-service": "Customer Service",
      "team-messaging": "Team Messaging",
      dig: "Dig",
    };
    const pageName = pageNames[currentPage] || "Dashboard";
    const companyName = company?.name || "";
    document.title = companyName
      ? `Chariot | ${companyName} | ${pageName}`
      : `Chariot | ${pageName}`;
  }, [currentPage, company]);

  // Fetch data on mount and when location changes
  useEffect(() => {
    if (selectedLocationId || isBridgeMode) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, isBridgeMode]);

  // Auto-refresh layout data every minute (only when user is not actively
  // interacting). usePoll reads the latest closure on each tick, so typing in
  // the search box no longer re-arms the timer on every keystroke.
  usePoll(() => {
    // Skip refresh if user is searching or has profile modal/dropdown open
    const isSearching = searchQuery.length > 0 || searchFocused;
    const isUsingProfile = profileModalOpen || profileDropdownOpen;
    if (isSearching || isUsingProfile) return undefined;
    return fetchDashboardData(true); // Silent refresh - no loading indicator
  }, 60000, { enabled: Boolean(selectedLocationId || isBridgeMode), immediate: false });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownOpen && !event.target.closest(".user-menu")) {
        setProfileDropdownOpen(false);
      }
      if (locationDropdownOpen && !event.target.closest(".sidebar-location")) {
        setLocationDropdownOpen(false);
      }
      if (roleViewDropdownOpen && !event.target.closest(".role-view-switcher")) {
        setRoleViewDropdownOpen(false);
      }
      if (inboxOpen && !event.target.closest(".inbox-menu")) {
        setInboxOpen(false);
      }
      if (addMenuOpen && !event.target.closest(".add-menu")) {
        setAddMenuOpen(false);
      }
      // Close search results when clicking outside. Selector must match the
      // wrapper below (.sidebar-search) — the dropdown renders inside it, so a
      // stale one closes the panel on its own clicks and strands searchFocused
      // at false while the input keeps DOM focus (no refocus event fires).
      if (searchFocused && !event.target.closest(".sidebar-search")) {
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileDropdownOpen, locationDropdownOpen, roleViewDropdownOpen, inboxOpen, addMenuOpen, searchFocused]);

  // ⌘K / Ctrl+K focuses the search box from anywhere.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        // Phone width: the search lives in the menu, so open the menu (not the
        // saved desktop setting) and focus once it has slid in.
        if (window.matchMedia(PHONE_QUERY).matches) {
          setMobileNavOpen(true);
          setTimeout(() => searchInputRef.current?.focus(), 260);
        } else {
          setSidebarOpen(true);
          searchInputRef.current?.focus();
        }
        setSearchFocused(true);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track mount state so a slow fetchDashboardData doesn't setState after
  // the layout has unmounted (e.g., user navigated away or logged out
  // mid-fetch). Without this, the 5 parallel fetches can keep firing
  // setCustomers/setOrders/etc. on a dead component.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const fetchDashboardData = async (silent = false) => {
    if (!silent && isMountedRef.current) setLoading(true);
    try {
      // Use fetchWithAuth so each request gets:
      //   1. A 30s AbortController timeout (no infinite spinner on a hung
      //      connection — Cloud Run cold-start, iPad LTE, etc.)
      //   2. Automatic 401 → refresh → retry-once (fixes empty-dashboard after
      //      a stale token; was previously a silent failure)
      // The location header is per-request via options.headers.
      const locationHeaders = getLocationHeaders();

      // Parse a response safely — returns null on non-ok or parse failure so
      // one failed endpoint doesn't nuke the whole dashboard load.
      const safeJson = async (res, label) => {
        if (!res || !res.ok) {
          if (res) console.warn(`[fetchDashboardData] ${label} returned ${res.status}`);
          return null;
        }
        try {
          return await res.json();
        } catch (err) {
          console.warn(`[fetchDashboardData] ${label} JSON parse failed:`, err.message);
          return null;
        }
      };

      // Skip endpoints the user is not permitted to call — avoids noisy 403s
      // and keeps the dashboard usable for users with limited access.
      const canFetchCustomers = can('customers.access');

      const guardedFetch = (path, allow) =>
        allow ? fetchWithAuth(path, { headers: locationHeaders }) : Promise.resolve(null);

      // Only what the shell actually feeds to pages: customers (Customers page
      // list + appointment modals) and locations. The orders / appointments /
      // revenue fetches that used to ride along were never consumed by any
      // page — and the appointments one was UNBOUNDED, running on every page
      // load and every minute for every user (Sep 2026 performance audit).
      // Promise.allSettled so one failure doesn't abort the rest
      const results = await Promise.allSettled([
        guardedFetch('/customers', canFetchCustomers),
        fetchWithAuth('/locations', { headers: locationHeaders }),
      ]);

      // Bail out if the component unmounted while requests were in flight.
      if (!isMountedRef.current) return;

      const [customersRes, locationsRes] =
        results.map((r) => (r.status === "fulfilled" ? r.value : null));

      const customersData = await safeJson(customersRes, "customers");
      const locationsData = await safeJson(locationsRes, "locations");

      if (!isMountedRef.current) return;

      if (customersData) setCustomers(customersData.data || []);
      if (locationsData) setLocations(locationsData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      if (!silent && isMountedRef.current) setLoading(false);
    }
  };

  return (
    <SocketProvider>
      <BadgeSocketRefresh
        onCallEvent={canAccessLiveCalls ? refreshCallsBadge : undefined}
        onSalesEvent={canAccessSalesSubmissions ? refreshSalesBadge : undefined}
      />
    <TimerProvider>
    <CustomerPreviewProvider>
    <SiteAlertsProvider>
    <div className="app-container">
      {/* Call notification toast disabled — will revisit later
      <CallNotificationToast onNavigate={(page) => setCurrentPage(page)} /> */}
      {/* Floating time clock bar (visible on all admin pages when tech is clocked in) */}
      <TimerBar />
      {/* Sitewide alert cards (Alert Center, display_style=card) — floating
          bottom-right on every admin page. Must stay right after TimerBar:
          the `.timer-bar ~ .site-alert-cards` rule lifts the stack above the
          time-clock bar when it's visible. */}
      <SiteAlertCards>
        {/* Lead alerts (new lead, claimed and not called, follow-up due) for
            people who work leads, on every page — the app frame shows them,
            not the OSR tab, so a rep on any page hears a lead arrive. They
            share the stack so nothing covers anything. */}
        {can('osr.access') ? <OsrAlerts /> : null}
        {/* Online bookings and the visits Hank booked, waiting in Pending
            Review: a card each until it is sent to Sales Sheets or archived
            (components/calls/BookingAlerts.js). */}
        {can('calls.booking_alerts') ? <BookingAlerts /> : null}
        {/* A website visitor asked Hank for a person: Accept to chat with them
            live; and a way back to my live chats (components/webChat/LiveChatAlerts.js). */}
        {can('web_chat.take') ? <LiveChatAlerts /> : null}
      </SiteAlertCards>
      {/* LEFT SIDEBAR */}
      <aside id="app-sidebar" className={`sidebar app-sidebar ${sidebarExpanded ? "open" : "closed"}${mobileNavOpen ? " mobile-open" : ""}`}>
        <div className="sidebar-header">
          <img
            src={
              sidebarExpanded
                ? "/images/Branding/Horizontal Logo/Chariot_HorizontalLogo_P1_FullColor_011226.png"
                : "/images/Branding/Mascot/Chariot_Mascot_P1_FullColor_011326.png"
            }
            alt="Chariot"
            className="logo-img"
          />
          {/* Collapse / expand button — always visible. Open mode: anchored
              to the header's top-right corner. Closed mode: hangs off the
              sidebar's right edge as a floating chip so the affordance is
              never hidden. Icon flips direction based on state. */}
          <button
            className="sidebar-collapse"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {sidebarOpen ? (
                <>
                  <path d="M11 17l-5-5 5-5" />
                  <path d="M18 17l-5-5 5-5" />
                </>
              ) : (
                <>
                  <path d="M13 17l5-5-5-5" />
                  <path d="M6 17l5-5-5-5" />
                </>
              )}
            </svg>
          </button>
        </div>

        {/* Resize handle — drag the right edge of the sidebar to adjust
            width. Only useful when expanded. Visible grip sits at vertical
            center; the underlying hit-area is a full-height strip. */}
        {sidebarOpen && (
          <div
            className={`sidebar-resize-handle ${sidebarResizing ? "resizing" : ""}`}
            onMouseDown={handleSidebarResizeStart}
            onDoubleClick={() => setSidebarWidth(SIDEBAR_WIDTH_DEFAULT)}
            title="Drag to resize · double-click to reset"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
          >
            <span className="sidebar-resize-grip" aria-hidden="true" />
          </div>
        )}

        {/* Location selector — pinned directly below the logo as the
            brand identity anchor (location > search per UX request). */}
        <div className="sidebar-location">
          <button
            className={`location-selector-trigger${isBridgeMode ? " bridge-active" : ""}`}
            onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
            disabled={locationLoading || accessibleLocations.length === 0}
            title={isBridgeMode ? "The Bridge — All locations" : (currentLocation?.name || "No location")}
          >
            {isBridgeMode ? (
              <span className="location-icon bridge-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              </span>
            ) : (
              <span className="location-icon" style={{"--icon-url": "url(/images/Icons/SVG/Location_Icon.svg)"}} />
            )}
            <span className="location-name">
              {isBridgeMode ? "The Bridge" : (currentLocation?.name || "No location")}
            </span>
            <span className={`location-chevron ${locationDropdownOpen ? "open" : ""}`}>▾</span>
          </button>

          {locationDropdownOpen && accessibleLocations.length > 0 && (
            <LocationDropdownPanel onClose={() => setLocationDropdownOpen(false)} />
          )}
        </div>

        {/* Global search — clicking the collapsed icon expands the sidebar
            and focuses the input so search is always one click away. */}
        <div
          className={`sidebar-search ${searchFocused ? "focused" : ""}`}
          onClick={() => {
            if (!sidebarExpanded) {
              setSidebarOpen(true);
              // Wait for sidebar's 0.3s width transition before focusing —
              // focusing while still collapsed would still work, but the
              // visual jump is jarring. 220ms gives just enough room.
              setTimeout(() => searchInputRef.current?.focus(), 220);
            }
          }}
        >
          {search.loading ? (
            <span className="search-spinner" role="status" aria-label="Searching" />
          ) : (
            <span className="search-icon" style={{"--icon-url": "url(/images/Icons/SVG/Search_Icon.svg)"}} />
          )}
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search customers, orders, vessels…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            // onFocus alone can't recover an already-focused input; clicking it
            // must reopen the panel too.
            onClick={() => setSearchFocused(true)}
            onKeyDown={(e) => search.onKeyDown(e, (item) => search.selectRef.current?.(item))}
            role="combobox"
            aria-expanded={searchFocused}
            aria-controls="global-search-results"
            aria-activedescendant={search.activeIndex >= 0 ? `search-opt-${search.activeIndex}` : undefined}
            aria-busy={search.loading}
            autoComplete="off"
          />
          <span className="search-shortcut">⌘K</span>
          {searchFocused && (
            <SearchResults
              search={search}
              onNavigateAll={(q) => navigate(`/app/customers?q=${encodeURIComponent(q)}`)}
            />
          )}
        </div>

        {/* Daily motivational message — dismissable for the day, auto-resets
            at midnight so the new day's message shows in the morning. Hidden
            in the collapsed-rail state via CSS. */}
        <DailyMessage />

        <nav className="sidebar-nav">
          <SidebarSection label="Main">

          {/* Each sidebar item gated by its own permission key */}
          {can('dashboard.access') && (
            <button
              className={`nav-item ${currentPage === "dashboard" ? "active" : ""}`}
              onClick={() => setCurrentPage("dashboard")}
              title="Dashboard"
            >
              <span className="icon svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/Dashboard_Icon.svg)"}} />
              <span className="label">Dashboard</span>
            </button>
          )}

          {can('workflow.access') && (
            <button
              className={`nav-item ${currentPage === "order-workflow" ? "active" : ""}`}
              onClick={() => setCurrentPage("order-workflow")}
              title="Order Workflow"
            >
              <span className="icon svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/Workflow_Icon.svg)"}} />
              <span className="label">Order Workflow</span>
            </button>
          )}

          {can('appointments.access') && (
            <button
              className={`nav-item ${currentPage === "appointments" ? "active" : ""}`}
              onClick={() => setCurrentPage("appointments")}
              title="Appointments"
            >
              <span className="icon svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/Calendar_Icon.svg)"}} />
              <span className="label">Appointments</span>
            </button>
          )}

          {can('timeclocks.access') && (
            <button
              className={`nav-item ${currentPage === "timeclocks" ? "active" : ""}`}
              onClick={() => setCurrentPage("timeclocks")}
              title="Time Clocks"
            >
              <span className="icon" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </span>
              <span className="label">Time Clocks</span>
            </button>
          )}

          </SidebarSection>
          <SidebarSection label="Customers">

          {/* Customers - accessible to admin, sales_manager, service_writer */}
          {canAccessCustomers && (
            <button
              className={`nav-item ${currentPage === "customers" ? "active" : ""}`}
              onClick={() => setCurrentPage("customers")}
              title="Customers"
            >
              <span className="icon svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/Subscribers_Icon.svg)"}} />
              <span className="label">Customers</span>
            </button>
          )}

          {/* Vessels - has its own vessels.access permission */}
          {can('vessels.access') && (
            <button
              className={`nav-item ${currentPage === "vessels" ? "active" : ""}`}
              onClick={() => setCurrentPage("vessels")}
              title="Vessels"
            >
              <span className="icon svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/Inventory_Icon.svg)"}} />
              <span className="label">Vessels</span>
            </button>
          )}

          {/* Poseidon+ - accessible to admin, sales_manager, service_writer */}
          {canAccessPoseidonPlus && (
            <button
              className={`nav-item ${currentPage === "poseidon-plus" ? "active" : ""}`}
              onClick={() => setCurrentPage("poseidon-plus")}
              title="Poseidon+"
            >
              <span className="icon svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/Poseidon+_Icon.svg)"}} />
              <span className="label">Poseidon+</span>
            </button>
          )}

          </SidebarSection>
          <SidebarSection label="Sales & Service">

          {/* Poseidon Live - admin, sales_manager, service_writer */}
          {canAccessShopmonkey && (
            <button
              className={`nav-item ${currentPage === "shopmonkey" ? "active" : ""}`}
              onClick={() => setCurrentPage("shopmonkey")}
              title="Poseidon Live"
            >
              <span className="icon svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/Trident_Icon.svg)"}} />
              <span className="label">Poseidon Live</span>
            </button>
          )}

          {/* Sales Intake - calls.access (the call tabs), osr.access (the OSR tab) or sales_estimates.access (Estimates) */}
          {canAccessLiveCalls && (
            <button
              className={`nav-item ${currentPage === "sales-intake" ? "active" : ""}`}
              onClick={() => setCurrentPage("sales-intake")}
              title="Sales Intake"
            >
              <span className="icon icon-badge-wrap">
                <span className="svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/Call_Icon.svg)"}} />
                {activeCallCount > 0 && (
                  <span className="lc-nav-badge">{activeCallCount}</span>
                )}
                {/* Leads waiting to be claimed (the OSR tab lives here). The
                    number comes from the lead alerts; nothing is polled here.
                    Blue, at the bottom corner, so it never reads as a call. */}
                {newLeadCount > 0 && (
                  <span className="osr-nav-badge" aria-label={`${newLeadCount} new lead${newLeadCount === 1 ? "" : "s"} waiting`}>{newLeadCount > 99 ? "99+" : newLeadCount}</span>
                )}
              </span>
              <span className="label">Sales Intake</span>
            </button>
          )}

          {/* Customer Service — Reputation, future tickets/surveys */}
          {canAccessCustomerService && (
            <button
              className={`nav-item ${currentPage === "customer-service" ? "active" : ""}`}
              onClick={() => setCurrentPage("customer-service")}
              title="Customer Service"
            >
              <span className="icon" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </span>
              <span className="label">Customer Service</span>
            </button>
          )}

          {/* Sales Submissions - admin, sales_manager, sales_support */}
          {canAccessSalesSubmissions && (
            <button
              className={`nav-item ${currentPage === "sales-submissions" ? "active" : ""}`}
              onClick={() => setCurrentPage("sales-submissions")}
              title="Sales Submissions"
            >
              <span className="icon icon-badge-wrap">
                <span className="svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/SalesSubmission_Icon.svg)"}} />
                {ssUncapturedCount > 0 && (
                  <span className="ss-nav-badge">{ssUncapturedCount}</span>
                )}
              </span>
              <span className="label">Sales Sheets</span>
            </button>
          )}

          {/* Dig — automated work-mining + outreach (docs/DIG_SYSTEM_PLAN.md) */}
          {canAccessDig && (
            <button
              className={`nav-item ${currentPage === "dig" ? "active" : ""}`}
              onClick={() => setCurrentPage("dig")}
              title="Dig"
            >
              <span className="icon svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/Dig_Icon_Gray.svg)"}} />
              <span className="label">Dig</span>
            </button>
          )}

          </SidebarSection>
          <SidebarSection label="Insights">

          {/* Reports - gated by reports.access; the payroll/commission sub-reports depend on it */}
          {can('reports.access') && (
            <button
              className={`nav-item ${currentPage === "reports" ? "active" : ""}`}
              onClick={() => setCurrentPage("reports")}
              title="Reports"
            >
              <span className="icon svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/Reports_Icon.svg)"}} />
              <span className="label">Reports</span>
            </button>
          )}

          {/* Marketing Hub — projects, events, inventory, field outreach,
              leads, and the Ads dashboards. OR-gated so a field rep with only
              marketing.field.access still gets the entry. */}
          {canAccessMarketingHub && (
            <button
              className={`nav-item ${currentPage === "marketing" ? "active" : ""}`}
              onClick={() => setCurrentPage("marketing")}
              title="Marketing Hub"
            >
              <span className="icon svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/Promo.svg)"}} />
              <span className="label">Marketing Hub</span>
            </button>
          )}

          {/* Parts & Inventory - Parts Orders and/or the inventory hub */}
          {canAccessPartsInventory && (
            <button
              className={`nav-item ${currentPage === "parts-inventory" ? "active" : ""}`}
              onClick={() => setCurrentPage("parts-inventory")}
              title="Parts & Inventory"
            >
              <span className="icon svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/PartsTable_Icon.svg)"}} />
              <span className="label">Parts &amp; Inventory</span>
            </button>
          )}
          </SidebarSection>
        </nav>

        <div className="sidebar-footer">
          {/* Settings - gated by settings.access */}
          {can('settings.access') && (
            <button
              className={`nav-item settings ${currentPage === "settings" ? "active" : ""}`}
              onClick={() => setCurrentPage("settings")}
              title={openCritical > 0 ? `Settings — ${openCritical} critical System Health incident${openCritical === 1 ? "" : "s"} open` : "Settings"}
            >
              <span className="icon icon-badge-wrap">
                <span className="svg-icon" style={{"--icon-url": "url(/images/Icons/SVG/Settings_Icon.svg)"}} />
                {openCritical > 0 && <span className="sh-nav-badge" aria-label={`${openCritical} critical incidents`}>{openCritical > 9 ? "9+" : openCritical}</span>}
              </span>
              <span className="label">Settings</span>
            </button>
          )}

          <HelpMenu
            isAdmin={realIsAdmin}
            onNavigateToSubmissions={() => setCurrentPage("help-submissions")}
          />
        </div>
      </aside>

      <MobileNavBackdrop open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

      {/* MAIN CONTENT */}
      <div className="main-wrapper">
        {/* Sitewide alert banners (Alert Center, display_style=banner) —
            full-width strips pinned above the top bar on every admin page. */}
        <SiteAlertBanners />
        {/* TOP BAR */}
        <header className="top-bar">
          <MobileNavButton open={mobileNavOpen} onOpen={() => setMobileNavOpen(true)} />
          {/* Page context slot — reserved for per-page sub-navigation
              (e.g. Poseidon Live's 9 sub-pages will render here as tabs/pills
              once that feature is wired). Intentionally empty until then. */}
          <div className="top-bar-context" data-page={currentPage} />

          <div className="top-bar-right">
            <SaveIndicator />
            {/* Saves made with no signal, until they've gone (utils/offlineOutbox.js). */}
            <OutboxStatus offline={offlineSession} />
            {confirmDialog}

            {/* View as / Dark mode / Edit profile / Logout all live inside the
                user-avatar dropdown below. Top-bar stays minimal. */}

            <div className="add-menu">
              <button className="icon-button" title="Add" onClick={() => setAddMenuOpen((v) => !v)}>
                <span className="top-bar-icon" style={{"--icon-url": "url(/images/Icons/SVG/Add_Icon.svg)"}} />
              </button>
              {addMenuOpen && can("orders.edit") && (
                <div className="add-menu-dropdown">
                  <button className="add-menu-item" onClick={() => { setAddMenuOpen(false); openNewOrder(); }}>
                    <span className="add-menu-item-icon" aria-hidden="true">＋</span> New Order
                  </button>
                </div>
              )}
            </div>

            {/* ─── Global customer messages (Shopmonkey feed today; the seed
                of the multi-source messaging platform) ─── */}
            <MessagesMenu writerScope={smWriterScope} />

            {/* ─── Unified Inbox (notifications + messages) ─── */}
            <InboxMenu
              open={inboxOpen}
              onToggle={() => setInboxOpen((v) => !v)}
              tab={inboxTab}
              onTabChange={setInboxTab}
              inboxRef={inboxRef}
            />
            <div className="user-menu">
              <button
                className="user-button"
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                title={user?.email}
              >
                {user?.profile_image_url ? (
                  <img
                    src={user.profile_image_url}
                    alt="Profile"
                    className="avatar-img"
                  />
                ) : (
                  <span className="avatar">
                    {user?.first_name?.[0]}
                    {user?.last_name?.[0]}
                  </span>
                )}
              </button>

              {profileDropdownOpen && (
                <div className="profile-dropdown">
                  <div className="profile-dropdown-header">
                    {user?.profile_image_url ? (
                      <img
                        src={user.profile_image_url}
                        alt="Profile"
                        className="profile-avatar-large-img"
                      />
                    ) : (
                      <div className="profile-avatar-large">
                        {user?.first_name?.[0]}
                        {user?.last_name?.[0]}
                      </div>
                    )}
                    <div className="profile-info">
                      <p className="profile-name">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="profile-email">{user?.email}</p>
                    </div>
                  </div>

                  <div className="profile-dropdown-menu">
                    {/* Edit Profile */}
                    <button
                      className="profile-dropdown-item"
                      onClick={() => {
                        setProfileModalOpen(true);
                        setProfileDropdownOpen(false);
                      }}
                    >
                      <span className="profile-dropdown-item-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </span>
                      <span className="profile-dropdown-item-label">Edit Profile</span>
                    </button>

                    {/* Dark / Light Mode toggle */}
                    <button
                      className="profile-dropdown-item"
                      onClick={() => setDarkMode(!darkMode)}
                    >
                      <span className="profile-dropdown-item-icon">
                        {darkMode ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="4" />
                            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                          </svg>
                        )}
                      </span>
                      <span className="profile-dropdown-item-label">
                        {darkMode ? "Light Mode" : "Dark Mode"}
                      </span>
                    </button>

                    {/* The new order page or the classic one (orders overhaul Phase 4). */}
                    {can("orders.access") && <OrderPageSwitch />}

                    {/* Poseidon Live scope — All writers vs Mine. Only for
                        users who can see all writers' orders. */}
                    {canSeeAllWriters && (
                      <div
                        className="profile-dropdown-item sm-scope-row"
                        title="Poseidon Live: see every writer's orders, or just yours — sticks until changed"
                      >
                        <span className="profile-dropdown-item-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                        </span>
                        <span className="profile-dropdown-item-label">
                          Poseidon Live
                        </span>
                        <span className="sm-scope-toggle">
                          <button
                            type="button"
                            className={`sm-scope-btn${smWriterScope === "all" ? " active" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              changeSmWriterScope("all");
                            }}
                          >
                            All
                          </button>
                          <button
                            type="button"
                            className={`sm-scope-btn${smWriterScope === "own" ? " active" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              changeSmWriterScope("own");
                            }}
                          >
                            Mine
                          </button>
                        </span>
                      </div>
                    )}

                    {/* View as — admin only. Submenu opens to the left. */}
                    {realIsAdmin && (
                      <div className="role-view-switcher" ref={roleViewDropdownRef}>
                        <button
                          className={`profile-dropdown-item profile-dropdown-item-submenu ${(viewAsRole || viewAsUserId) ? "active" : ""} ${roleViewDropdownOpen ? "open" : ""}`}
                          onClick={() => setRoleViewDropdownOpen(!roleViewDropdownOpen)}
                          title="Preview as another role or specific user"
                        >
                          <span className="profile-dropdown-item-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </span>
                          <span className="profile-dropdown-item-label">
                            View as
                            {(viewAsRole || viewAsUserId) && (
                              <span className="profile-dropdown-badge">
                                {viewAsUserId
                                  ? (viewAsUserData?.name || "user")
                                  : (ROLE_LABELS[viewAsRole] || viewAsRole)}
                              </span>
                            )}
                          </span>
                          <span className="profile-dropdown-item-chevron">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 18l6-6-6-6" />
                            </svg>
                          </span>
                        </button>

                        {roleViewDropdownOpen && (
                          <div className="role-view-dropdown">
                            <div className="role-view-dropdown-header">Preview as role</div>
                            {PREVIEWABLE_ROLES.map(({ value: roleValue, label }) => (
                              <button
                                key={roleValue}
                                className={`role-view-dropdown-item ${viewAsRole === roleValue ? "active" : ""}`}
                                onClick={() => {
                                  setViewAsRole(roleValue === viewAsRole ? null : roleValue);
                                  setRoleViewDropdownOpen(false);
                                }}
                              >
                                {label}
                                {viewAsRole === roleValue && <span className="role-view-check">✓</span>}
                              </button>
                            ))}

                            <div className="role-view-dropdown-divider" />
                            <div className="role-view-dropdown-header">Preview as specific user</div>
                            <input
                              type="text"
                              placeholder="Search users..."
                              value={userPreviewSearch}
                              onChange={async (e) => {
                                setUserPreviewSearch(e.target.value);
                                if (previewUsers.length === 0) {
                                  try {
                                    const response = await fetch(`${API_URL}/admin/users?limit=100`, {
                                      headers: { ...getAuthHeaders() },
                                    });
                                    if (response.ok) {
                                      const data = await response.json();
                                      setPreviewUsers(data.users || []);
                                    }
                                  } catch (err) { /* ignore */ }
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                width: "calc(100% - 16px)",
                                margin: "6px 8px 4px",
                                padding: "6px 10px",
                                border: "1px solid #d1d5db",
                                borderRadius: 6,
                                fontSize: 13,
                              }}
                            />
                            <div style={{ maxHeight: 200, overflowY: "auto" }}>
                              {previewUsers
                                .filter((u) => {
                                  const q = userPreviewSearch.trim().toLowerCase();
                                  if (!q) return false;
                                  return (
                                    (u.first_name || "").toLowerCase().includes(q) ||
                                    (u.last_name || "").toLowerCase().includes(q) ||
                                    (u.email || "").toLowerCase().includes(q)
                                  );
                                })
                                .slice(0, 20)
                                .map((u) => (
                                  <button
                                    key={u.id}
                                    className={`role-view-dropdown-item ${viewAsUserId === u.id ? "active" : ""}`}
                                    onClick={async () => {
                                      const ok = await previewAsUser(u.id);
                                      if (!ok) {
                                        showToast.error(
                                          `Couldn't start preview as ${u.first_name} ${u.last_name}`
                                        );
                                      }
                                      setRoleViewDropdownOpen(false);
                                      setUserPreviewSearch("");
                                    }}
                                  >
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                                      <span style={{ fontSize: 13 }}>{u.first_name} {u.last_name}</span>
                                      <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{ROLE_LABELS[u.role] || u.role}</span>
                                    </div>
                                    {viewAsUserId === u.id && <span className="role-view-check">✓</span>}
                                  </button>
                                ))}
                            </div>

                            {(viewAsRole || viewAsUserId) && (
                              <>
                                <div className="role-view-dropdown-divider" />
                                <button
                                  className="role-view-dropdown-item exit"
                                  onClick={async () => {
                                    setViewAsRole(null);
                                    await previewAsUser(null);
                                    setRoleViewDropdownOpen(false);
                                    setUserPreviewSearch("");
                                  }}
                                >
                                  Exit preview
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="profile-dropdown-divider" />

                  {/* Admin-only: audit who can reach the page behind this
                      menu, and grant/revoke without leaving it. */}
                  {realIsAdmin && (
                    <button
                      className="profile-dropdown-item"
                      onClick={() => {
                        setProfileDropdownOpen(false);
                        setPagePermsOpen(true);
                      }}
                    >
                      <span className="profile-dropdown-item-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </span>
                      <span className="profile-dropdown-item-label">Who can see this page?</span>
                    </button>
                  )}

                  <button
                    className="profile-dropdown-item logout"
                    onClick={signOut}
                  >
                    <span className="profile-dropdown-item-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                    </span>
                    <span className="profile-dropdown-item-label">Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Role / User Preview Banner */}
        {(viewAsRole || viewAsUserId) && (
          <div className="role-preview-banner">
            <span className="role-preview-banner-icon">👁</span>
            <span>
              {viewAsUserId && viewAsUserData ? (
                <>
                  Viewing as{" "}
                  <strong>
                    {viewAsUserData.name || "user"} (
                    {ROLE_LABELS[viewAsUserData.role] || viewAsUserData.role})
                  </strong>{" "}
                  — you're seeing their real access and data. Read-only:
                  changes are blocked until you exit.
                </>
              ) : (
                <>
                  Previewing role{" "}
                  <strong>{ROLE_LABELS[viewAsRole] || viewAsRole}</strong> —
                  permissions enforced end-to-end (read-only); personal
                  filters like "assigned to me" still use your account.
                </>
              )}
            </span>
            <button className="role-preview-banner-exit" onClick={async () => {
              setViewAsRole(null);
              await previewAsUser(null);
            }}>
              Exit Preview
            </button>
          </div>
        )}

        {/* PAGE CONTENT */}
        <main className={`page-content${orderId || currentPage === "settings" || currentPage === "order-workflow" || currentPage === "workflow" ? " no-padding" : ""}`}>
          {/* One Suspense boundary for every lazy page: the first visit to a page
              shows this while its chunk downloads; later visits are instant. */}
          <ChunkErrorBoundary>
          <React.Suspense fallback={<div className="loading">Loading…</div>}>
          {loading && <div className="loading">Loading...</div>}

          {/* Admin-only pages */}
          { currentPage === "dashboard" && can('dashboard.access') && (
            <DashboardPage />
          )}

          {
            currentPage === "customers" &&
            !customerId &&
            canAccessCustomers && (
              <CustomersPage onRefresh={fetchDashboardData} />
            )}

          {
            currentPage === "customers" &&
            customerId &&
            canAccessCustomers && <CustomerDetail customerId={customerId} />}

          {
            currentPage === "vessels" &&
            !vehicleId &&
            !engineId &&
            can('vessels.access') && <VesselsPage />}

          {
            currentPage === "vessels" &&
            vehicleId &&
            can('vessels.access') && <VesselDetail vehicleId={vehicleId} />}

          {
            currentPage === "vessels" &&
            engineId &&
            can('vessels.access') && <EngineDetail engineId={engineId} />}

          {/* Bridge - unified orders & workflow command center */}
          { currentPage === "order-workflow" && can('workflow.access') && (
            <OrderWorkflowPage />
          )}

          {/* Payment Terminal - a card charge with no Chariot order behind it */}
          { currentPage === "payment-terminal" && can('terminal.access') && (
            <PaymentTerminalPage />
          )}

          {/* Order detail - accessed from Bridge by clicking an order */}
          {
            currentPage === "orders" &&
            orderId &&
            can('orders.access') && <OrderDetail orderId={orderId} />}

          {/* /app/orders with no order (including the retired /app/orders/new)
              goes to the Order Workflow board. New orders start from the
              New Order form. */}
          {
            currentPage === "orders" &&
            !orderId && (
              <Navigate to="/app/order-workflow" replace />
            )}

          {
            currentPage === "appointments" &&
            can('appointments.access') && (
              <AppointmentsPage
                customers={customers}
                locations={locations}
                selectedLocationId={selectedLocationId}
                isBridgeMode={isBridgeMode}
                onRefresh={fetchDashboardData}
              />
            )}

          {currentPage === "timeclocks" && can('timeclocks.access') && (
            <TimeClocksPage selectedLocationId={selectedLocationId} />
          )}

          { currentPage === "reports" && can('reports.access') && (
            <React.Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading reports…</div>}>
              <ReportsPage reportKey={reportKey} />
            </React.Suspense>
          )}

          { currentPage === "marketing" && canAccessMarketingHub && (
            <React.Suspense fallback={<div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading marketing…</div>}>
              <MarketingHubPage />
            </React.Suspense>
          )}

          { currentPage === "parts-inventory" && canAccessPartsInventory && (
            <PartsInventoryPage />
          )}

          {/* Redirect /app/workflow to Bridge */}
          { currentPage === "workflow" && can('workflow.access') && (
            <OrderWorkflowPage />
          )}

          { currentPage === "settings" && can('settings.access') && (
            <SettingsPage />
          )}

          {/* Poseidon+ - accessible to admin, sales_manager, service_writer */}
          {
            currentPage === "poseidon-plus" &&
            canAccessPoseidonPlus && <PoseidonPlusPage />}

          {/* Poseidon Live - accessible to admin, sales_manager, service_writer */}
          { currentPage === "shopmonkey" && canAccessShopmonkey && (
            <ShopmonkeyLive writerScope={smWriterScope} />
          )}

          {/* Sales Intake - the call tabs need calls.access; the OSR tab needs
              osr.access; Estimates needs sales_estimates.access. Someone
              without the call tabs gets OSR and/or Estimates on their own,
              with Sales Intake's short tab strip. */}
          { currentPage === "sales-intake" && canAccessLiveCalls && (
            can('calls.access') ? <LiveCallsPage />
              : can('sales_estimates.access') && (salesIntakeTab === "estimates" || !can('osr.access')) ? <SalesIntakeEstimates />
                : <SalesIntakeOsr />
          )}

          {/* Sales Submissions - accessible to admin, sales_manager, sales_support */}
          { currentPage === "sales-submissions" && canAccessSalesSubmissions && (
            <SalesSubmissionsPage />
          )}

          {/* Payment Event Log - admin-only forensic view */}
          { currentPage === "payment-events" && can('payments.events.view') && (
            <PaymentEventLogPage />
          )}

          {/* Payment Vault Test - admin-only sandbox for PCI Vault + Square */}
          { currentPage === "payment-vault-test" && can('payments.processor.switch') && (
            <PaymentVaultTestPage />
          )}

          {/* Work Heat Map - admin-only analytics tool */}
          { currentPage === "work-heat-map" && can('adminTools.workHeatMap.access') && (
            <WorkHeatMapPage />
          )}

          {/* Live Dispatch - field tech location map (CHARIOT app v0.1) */}
          { currentPage === "dispatch" && can('dispatch.access') && (
            <DispatchPage />
          )}

          {/* Service Geozones - draw/test service territories + drive-time zones */}
          { currentPage === "geozones" && can('adminTools.geozones.access') && (
            <GeozonesPage />
          )}

          {/* Team Messaging - admin-only testing area for new internal chat */}
          { currentPage === "team-messaging" && can('messaging.access') && (
            <TeamMessagingPage />
          )}

          {/* Help Submissions - admin only */}
          { currentPage === "help-submissions" && realIsAdmin && (
            <HelpSubmissionsPage />
          )}

          {/* Customer Service — wrapper page that owns its own sub-routes */}
          { currentPage === "customer-service" && canAccessCustomerService && (
            <CustomerServicePage />
          )}

          {/* Dig — wrapper page that owns its own sub-routes */}
          { currentPage === "dig" && canAccessDig && (
            <DigPage />
          )}

          {/* Access Denied message for unauthorized page access */}
          {
            ((currentPage === "dashboard" && !can('dashboard.access')) ||
              (currentPage === "customers" && !can('customers.access')) ||
              (currentPage === "vessels" && !can('vessels.access')) ||
              (currentPage === "orders" && !can('orders.access')) ||
              (currentPage === "appointments" && !can('appointments.access')) ||
              (currentPage === "dispatch" && !can('dispatch.access')) ||
              (currentPage === "reports" && !can('reports.access')) ||
              (currentPage === "marketing" && !canAccessMarketingHub) ||
              (currentPage === "parts-inventory" && !canAccessPartsInventory) ||
              (currentPage === "workflow" && !can('workflow.access')) ||
              (currentPage === "settings" && !can('settings.access')) ||
              (currentPage === "poseidon-plus" && !can('plus.access')) ||
              (currentPage === "shopmonkey" && !can('shopmonkey.access')) ||
              (currentPage === "sales-intake" && !canAccessLiveCalls) ||
              (currentPage === "sales-submissions" && !can('sales_submissions.access')) ||
              (currentPage === "payment-events" && !can('payments.events.view')) ||
              (currentPage === "payment-vault-test" && !can('payments.processor.switch')) ||
              (currentPage === "payment-terminal" && !can('terminal.access')) ||
              (currentPage === "work-heat-map" && !can('adminTools.workHeatMap.access')) ||
              (currentPage === "geozones" && !can('adminTools.geozones.access')) ||
              (currentPage === "team-messaging" && !can('messaging.access')) ||
              (currentPage === "customer-service" && !canAccessCustomerService) ||
              (currentPage === "dig" && !canAccessDig)) && (
              <div
                className="access-denied"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "60vh",
                  textAlign: "center",
                  color: "#666",
                }}
              >
                <h2 style={{ fontSize: "24px", marginBottom: "16px" }}>
                  Access Denied
                </h2>
                <p style={{ fontSize: "16px", marginBottom: "24px" }}>
                  You don't have permission to access this page.
                </p>
                <p style={{ fontSize: "14px", color: "#999" }}>
                  Contact your administrator if you believe this is an error.
                </p>
              </div>
            )}
          </React.Suspense>
          </ChunkErrorBoundary>
        </main>
      </div>

      {/* NEW ORDER FORM — opened by openNewOrder() (top-bar +, the board) */}
      <NewOrderHost />

      {/* Resolved from the live route, so opening it on Poseidon Live ▸
          Collections audits that tab, not just the parent page. */}
      {pagePermsOpen && (
        <PagePermissionsModal
          path={routerLocation.pathname}
          onClose={() => setPagePermsOpen(false)}
        />
      )}

      {profileModalOpen && (
        <ChunkErrorBoundary>
          <React.Suspense fallback={null}>
            <EditProfileModal onClose={() => setProfileModalOpen(false)} />
          </React.Suspense>
        </ChunkErrorBoundary>
      )}
      {/* Global customer preview drawer — slides in from the right anywhere in the admin app */}
      <CustomerPreviewDrawer />
    </div>
    </SiteAlertsProvider>
    </CustomerPreviewProvider>
    </TimerProvider>
    </SocketProvider>
  );
}

export default AdminLayout;
